import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Alert, AlertTitle,
  List, ListItem, ListItemText, ListItemIcon, CircularProgress,
  Divider, Chip, TextField, Grid
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import ModernLayout from '../common/ModernLayout';
import { GradientButton } from '../common/ModernButton';
import UniFiCloudAPIService from './UniFiCloudAPIService';

const UniFiDiagnostics = () => {
  const [diagnostics, setDiagnostics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [testResults, setTestResults] = useState(null);

  useEffect(() => {
    runDiagnostics();
  }, []);

  const runDiagnostics = async () => {
    setLoading(true);
    setDiagnostics([]);
    
    const results = [];

    // Test 1: Check if we're in a browser environment
    results.push({
      id: 'browser',
      title: 'Browser Environment',
      status: typeof window !== 'undefined' ? 'success' : 'error',
      message: typeof window !== 'undefined' ? 'Running in browser environment' : 'Not in browser environment',
      details: `User Agent: ${navigator.userAgent}`
    });

    // Test 2: Check if fetch is available
    results.push({
      id: 'fetch',
      title: 'Fetch API',
      status: typeof fetch !== 'undefined' ? 'success' : 'error',
      message: typeof fetch !== 'undefined' ? 'Fetch API is available' : 'Fetch API not available',
      details: 'Required for making HTTP requests'
    });

    // Test 3: Check localStorage
    results.push({
      id: 'localStorage',
      title: 'Local Storage',
      status: typeof localStorage !== 'undefined' ? 'success' : 'error',
      message: typeof localStorage !== 'undefined' ? 'Local storage is available' : 'Local storage not available',
      details: 'Required for storing configuration'
    });

    // Test 4: Check UniFi configuration
    const cloudConfig = localStorage.getItem('unifiCloudConfig');
    const controllerConfig = localStorage.getItem('unifiConfig');
    
    if (cloudConfig || controllerConfig) {
      results.push({
        id: 'config',
        title: 'UniFi Configuration',
        status: 'success',
        message: 'UniFi configuration found',
        details: cloudConfig ? 'Cloud configuration detected' : 'Controller configuration detected'
      });
    } else {
      results.push({
        id: 'config',
        title: 'UniFi Configuration',
        status: 'warning',
        message: 'No UniFi configuration found',
        details: 'Please run the setup wizard to configure UniFi integration'
      });
    }

    // Test 5: Basic internet connectivity
    try {
      const response = await fetch('https://httpbin.org/get', { method: 'HEAD' });
      results.push({
        id: 'internet',
        title: 'Internet Connectivity',
        status: response.ok ? 'success' : 'error',
        message: response.ok ? 'Internet connection working' : 'Internet connection failed',
        details: `Response: ${response.status} ${response.statusText}`
      });
    } catch (error) {
      results.push({
        id: 'internet',
        title: 'Internet Connectivity',
        status: 'error',
        message: 'Internet connection failed',
        details: error.message
      });
    }

    // Test 6: UniFi Cloud connectivity
    try {
      const response = await fetch('https://unifi.ui.com', { method: 'HEAD' });
      results.push({
        id: 'unifi-cloud',
        title: 'UniFi Cloud Connectivity',
        status: response.ok ? 'success' : 'error',
        message: response.ok ? 'Can reach UniFi Cloud' : 'Cannot reach UniFi Cloud',
        details: `Response: ${response.status} ${response.statusText}`
      });
    } catch (error) {
      results.push({
        id: 'unifi-cloud',
        title: 'UniFi Cloud Connectivity',
        status: 'error',
        message: 'Cannot reach UniFi Cloud',
        details: error.message
      });
    }

    setDiagnostics(results);
    setLoading(false);
  };

  const testApiKey = async () => {
    if (!apiKey.trim()) {
      setTestResults({ success: false, error: 'Please enter an API key' });
      return;
    }

    setLoading(true);
    setTestResults(null);

    try {
      const cloudService = new UniFiCloudAPIService();
      cloudService.initialize({ apiKey: apiKey.trim() });

      // Test connectivity
      const connectivityTest = await cloudService.testConnectivity();
      console.log('Connectivity test:', connectivityTest);

      if (!connectivityTest.success) {
        setTestResults({ success: false, error: `Connectivity test failed: ${connectivityTest.error}` });
        setLoading(false);
        return;
      }

      // Test API connection
      const connectionTest = await cloudService.testConnection();
      console.log('Connection test:', connectionTest);

      if (connectionTest.success) {
        setTestResults({ 
          success: true, 
          message: 'API key is valid and connection successful',
          data: connectionTest.data
        });
      } else {
        setTestResults({ success: false, error: `API connection failed: ${connectionTest.error}` });
      }
    } catch (error) {
      setTestResults({ success: false, error: error.message });
    }

    setLoading(false);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      default:
        return 'info';
    }
  };

  return (
    <ModernLayout title="UniFi Diagnostics">
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
              UniFi Diagnostics
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Troubleshoot UniFi connection issues
            </Typography>
          </Box>
          <GradientButton
            startIcon={<RefreshIcon />}
            onClick={runDiagnostics}
            disabled={loading}
          >
            {loading ? 'Running...' : 'Run Diagnostics'}
          </GradientButton>
        </Box>
      </Box>

      {/* API Key Test */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Test API Key
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter your UniFi Cloud API key to test the connection
          </Typography>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="UniFi Cloud API Key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                type="password"
                placeholder="Enter your API key here"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                onClick={testApiKey}
                disabled={loading || !apiKey.trim()}
              >
                {loading ? <CircularProgress size={20} /> : 'Test Connection'}
              </Button>
            </Grid>
          </Grid>

          {testResults && (
            <Alert severity={testResults.success ? 'success' : 'error'} sx={{ mt: 2 }}>
              <AlertTitle>{testResults.success ? 'Success' : 'Error'}</AlertTitle>
              {testResults.success ? testResults.message : testResults.error}
              {testResults.data && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" fontFamily="monospace">
                    {JSON.stringify(testResults.data, null, 2)}
                  </Typography>
                </Box>
              )}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Diagnostics Results */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            System Diagnostics
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {diagnostics.map((test, index) => (
                <React.Fragment key={test.id}>
                  <ListItem>
                    <ListItemIcon>
                      {getStatusIcon(test.status)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" fontWeight="medium">
                            {test.title}
                          </Typography>
                          <Chip 
                            label={test.status} 
                            color={getStatusColor(test.status)}
                            size="small"
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {test.message}
                          </Typography>
                          {test.details && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                              {test.details}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < diagnostics.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </ModernLayout>
  );
};

export default UniFiDiagnostics; 